import { type ElementType, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router';
import isPropValid from '@emotion/is-prop-valid';
import { StyleSheetManager } from 'styled-components';
import * as Sentry from '@sentry/react';

import getAppLanguageSelector from '/business/shared/state/selectors/language/getAppLanguageSelector';
import getIsWhitelabelConfigSetSelector from '/business/shared/state/selectors/whitelabel/getIsWhitelabelConfigSetSelector';

import useGetCoreRouter from '/business/shared/components/router/useGetCoreRouter';

import axiosSetup from '/shared/util/axiosSetup';
import isMouse from '/shared/util/isMouse';
import assignSessionKey from '/shared/util/assignSessionKey';

import { hotjarBusinessUid, sentrySixAgencyDebuggingEnabled, sentrySixAgencyInjestionUrl } from '/shared/util/Statics';
import Hotjar from '/business/shared/Hotjar';
import GA from '/business/shared/GA';

import Context from './contexts/Context';
import setWhitelabelConfig from '/business/shared/state/actions/whitelabel/setWhitelabelConfig';
import type { WhitelabelConfig } from '/business/shared/state/actions/whitelabel/setWhitelabelConfig';
import type { AppDispatch } from '/business/shared/state/store';
import type { PartialAppContext } from '/index.d';

axiosSetup();
isMouse();
assignSessionKey();

if (sentrySixAgencyDebuggingEnabled && sentrySixAgencyInjestionUrl !== '') {
  Sentry.init({
    dsn: sentrySixAgencyInjestionUrl,
    defaultIntegrations: false,
    integrations: [
      // disabled session + breadcrumbs by turning off defaults
      Sentry.browserApiErrorsIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.httpContextIntegration(),
      Sentry.functionToStringIntegration(),
      Sentry.inboundFiltersIntegration(),
      Sentry.globalHandlersIntegration(),
      Sentry.linkedErrorsIntegration(),
      // Sentry.breadcrumbsIntegration(),
      /*
      using browserSessionIntegration causes noticable browser-lag, so not included
       */
      Sentry.replayIntegration(),
    ],

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      //  apiEndpoint,
    ],

    // Tracing
    tracesSampleRate: 0, //  Capture 0 traces (performance issues)
    // Session Replay
    replaysSessionSampleRate: 0, // [was 0.1] This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1, // [was 1] If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

Hotjar.initialize(hotjarBusinessUid);
GA.initialize();

// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: string, targetProp: string | ElementType) {
  if (typeof targetProp === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
}

export function App({ config }: { config: PartialAppContext & WhitelabelConfig }) {
  const dispatch = useDispatch<AppDispatch>();
  const appLang = useSelector(getAppLanguageSelector);
  const isWhitelabelConfigSet = useSelector(getIsWhitelabelConfigSetSelector);

  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n?.languages?.[0] || 'en');
  const corePrimaryRouter = useGetCoreRouter(config);

  async function changeLanguage(mutedLang: string) {
    await i18n.changeLanguage(mutedLang);
    setLanguage(mutedLang);
  }
  useEffect(() => {
    if (language !== appLang) {
      changeLanguage(appLang);
    }
  }, [language, appLang]);

  useEffect(() => {
    // Add whitelabel configs to application state, this is so configs are available in state selectors
    dispatch(setWhitelabelConfig(config));
  }, [dispatch, config]);

  if (!isWhitelabelConfigSet) {
    return null;
  }

  return (
    <Context.Provider value={config}>
      <StyleSheetManager enableVendorPrefixes shouldForwardProp={shouldForwardProp}>
        <RouterProvider router={corePrimaryRouter} />
      </StyleSheetManager>
    </Context.Provider>
  );
}

export default App;
